import type { PostHogConfig } from 'posthog-js';

/**
 * The embedded valuation widget and its integration-test page. The embed loads on every
 * page of every client website, closed, so these paths carry two orders of magnitude more
 * sessions than the CRM and would exhaust the analytics quota on their own.
 */
export function isWidgetPath(pathname: string): boolean {
  return /^\/[a-z]{2}\/(avm|integration-test)(\/|\?|$)/.test(pathname);
}

/**
 * posthog-js options for the current surface. The CRM keeps the full capture set. The
 * widget sends nothing on its own: no pageviews, pageleaves, clicks, vitals or replay.
 * Only exceptions (lead-loss errors) and the explicit `widget_estimate_shown` event leave
 * it, so a passive embed on a client page costs zero events.
 */
export function posthogOptionsFor(pathname: string): Partial<PostHogConfig> {
  const base: Partial<PostHogConfig> = {
    // The App Router navigates client-side, so pageviews are captured manually in
    // PostHogPageView instead of on full page loads only.
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: false,
    capture_exceptions: true,
  };
  if (!isWidgetPath(pathname)) return base;
  return {
    ...base,
    capture_pageleave: false,
    autocapture: false,
    capture_performance: false,
    capture_dead_clicks: false,
    rageclick: false,
    disable_session_recording: true,
  };
}
